import { RatingBonusSettings, RatingBonusNote } from '../../../components/admin/staff/RatingBonus';
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, CalendarCheck, RefreshCw, Send, FileDown, IndianRupee, X, Trash2, Plus } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { fmtINR, formatErr, notifyErr, inputCls, todayISO } from '../../../components/admin/billing/shared';
import SalaryRevisionsCard from './SalaryRevisionsCard';

const METHODS = ['Bank', 'Cash', 'UPI', 'Cheque', 'Other'];

const PayrollPage = () => {
  const { authHeader } = useAuth();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [err, setErr] = useState('');
  const [expanded, setExpanded] = useState({});
  // Payment modal state
  const [payOpen, setPayOpen] = useState(null); // { staff_id, staff_name, net_payable }
  const [payments, setPayments] = useState({ rows: [], total_paid: 0 });
  const [payForm, setPayForm] = useState({ date: todayISO(), amount: 0, method: 'Bank', reference: '', notes: '' });
  const [paySaving, setPaySaving] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  // Advance form state (per-row inline)
  const [advForm, setAdvForm] = useState({});   // { [staff_id]: { amount, note, date } }
  const [advSaving, setAdvSaving] = useState(null);

  const submitAdvance = async (row) => {
    const f = advForm[row.staff_id] || {};
    const amt = Number(f.amount);
    if (!amt || amt <= 0) { setErr('Enter an advance amount greater than zero.'); return; }
    setAdvSaving(row.staff_id);
    setErr('');
    try {
      await axios.post(
        `${API}/staff/${row.staff_id}/advances`,
        { staff_id: row.staff_id, amount: amt, date: f.date || todayISO(), note: f.note || '' },
        { headers: authHeader() },
      );
      setAdvForm((p) => ({ ...p, [row.staff_id]: { amount: '', note: '', date: todayISO() } }));
      compute();
    } catch (e) { setErr(formatErr(e)); }
    finally { setAdvSaving(null); }
  };

  const deleteAdvance = async (row, advId) => {
    if (!window.confirm('Delete this advance? It will no longer be deducted.')) return;
    try {
      await axios.delete(`${API}/staff/${row.staff_id}/advances/${advId}`, { headers: authHeader() });
      compute();
    } catch (e) { setErr(formatErr(e)); }
  };

  const compute = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const { data: out } = await axios.post(`${API}/payroll/compute`, { year, month }, { headers: authHeader() });
      setData(out);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, year, month]);

  useEffect(() => { compute(); }, [compute]);

  const postToExpenses = async () => {
    if (!window.confirm(`Post all net-payable amounts as "Salary & Wages" expenses for ${year}-${String(month).padStart(2, '0')}? Idempotent — won't double-post.`)) return;
    setPosting(true);
    try {
      const { data: out } = await axios.post(`${API}/payroll/post-to-expenses`, { year, month }, { headers: authHeader() });
      alert(`Created ${out.created} expense entries (${out.skipped} skipped — already posted or zero net).`);
    } catch (e) { notifyErr(e); }
    finally { setPosting(false); }
  };

  const downloadStatementPdf = async (sid, name) => {
    try {
      const resp = await axios.get(
        `${API}/payroll/staff/${sid}/pdf?year=${year}&month=${month}`,
        { headers: authHeader(), responseType: 'blob' },
      );
      const url = URL.createObjectURL(resp.data);
      const safeName = (name || 'staff').replace(/\s+/g, '_');
      const link = document.createElement('a');
      link.href = url;
      link.download = `Payroll_${year}-${String(month).padStart(2, '0')}_${safeName}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (e) {
      notifyErr(e);
    }
  };

  const openPaymentModal = async (row) => {
    setPayOpen({ staff_id: row.staff_id, staff_name: row.staff_name, net_payable: row.net_payable });
    setPayForm({ date: todayISO(), amount: 0, method: 'Bank', reference: '', notes: '' });
    setPayLoading(true);
    try {
      const { data: out } = await axios.get(
        `${API}/payroll/staff/${row.staff_id}/payments?year=${year}&month=${month}`,
        { headers: authHeader() },
      );
      setPayments(out || { rows: [], total_paid: 0 });
    } catch (e) {
      setPayments({ rows: [], total_paid: 0 });
      notifyErr(e);
    } finally {
      setPayLoading(false);
    }
  };

  const closePaymentModal = () => { setPayOpen(null); setPayments({ rows: [], total_paid: 0 }); };

  const refreshPayments = async () => {
    if (!payOpen) return;
    try {
      const { data: out } = await axios.get(
        `${API}/payroll/staff/${payOpen.staff_id}/payments?year=${year}&month=${month}`,
        { headers: authHeader() },
      );
      setPayments(out || { rows: [], total_paid: 0 });
    } catch (e) { notifyErr(e); }
  };

  const savePayment = async () => {
    if (!payOpen) return;
    const amt = Number(payForm.amount || 0);
    if (amt <= 0) { alert('Amount must be greater than zero.'); return; }
    setPaySaving(true);
    try {
      await axios.post(
        `${API}/payroll/staff/${payOpen.staff_id}/payment`,
        { year, month, ...payForm, amount: amt },
        { headers: authHeader() },
      );
      setPayForm({ date: todayISO(), amount: 0, method: 'Bank', reference: '', notes: '' });
      await refreshPayments();
      compute(); // refresh top-level paid / pending roll-up
    } catch (e) { notifyErr(e); }
    finally { setPaySaving(false); }
  };

  const deletePayment = async (pid) => {
    if (!payOpen) return;
    if (!window.confirm('Delete this payment entry? This also removes the matching expense row.')) return;
    try {
      await axios.delete(
        `${API}/payroll/staff/${payOpen.staff_id}/payment/${pid}`,
        { headers: authHeader() },
      );
      await refreshPayments();
      compute(); // refresh top-level paid / pending roll-up
    } catch (e) { notifyErr(e); }
  };

  const outstanding = payOpen
    ? Math.max(0, Number(payOpen.net_payable || 0) - Number(payments.total_paid || 0))
    : 0;

  return (
    <div className="space-y-4 p-4 sm:p-6 bg-slate-50 min-h-full" data-testid="payroll-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CalendarCheck className="text-teal-600" size={22} />
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Payroll</h1>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className={inputCls} data-testid="payroll-year">
            {Array.from({ length: 5 }).map((_, i) => {
              const y = today.getFullYear() - 2 + i;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className={inputCls} data-testid="payroll-month">
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{new Date(2000, m - 1, 1).toLocaleString('default', { month: 'long' })}</option>
            ))}
          </select>
          <button onClick={compute} className="text-sm bg-slate-100 hover:bg-slate-200 border border-slate-300 text-slate-800 px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="recompute-btn">
            <RefreshCw size={14} /> Recompute
          </button>
          <button onClick={postToExpenses} disabled={posting || !data || data.totals.net === 0} className="text-sm bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white font-semibold px-3 py-2 rounded-lg inline-flex items-center gap-1.5" data-testid="post-payroll-btn">
            {posting ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
            {posting ? 'Posting…' : 'Post to Expenses'}
          </button>
        </div>
      </div>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>}

      <SalaryRevisionsCard onApplied={compute} />

      {loading && !data ? (
        <div className="text-center py-12 text-slate-500"><Loader2 className="inline animate-spin mr-2" size={20} /> Computing payroll…</div>
      ) : !data || !data.rows.length ? (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-400 text-sm">No staff payroll to compute. Add staff first.</div>
      ) : (
        <>
          {/* Financial totals */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: 'Base Salary', value: data.totals.base, color: 'text-slate-900' },
              { label: 'OT Pay', value: data.totals.ot, color: 'text-amber-700' },
              { label: data.totals.rating_bonus ? 'Incentive + rating bonus' : 'Incentive', value: (data.totals.incentive || 0) + (data.totals.rating_bonus || 0), color: 'text-emerald-700', testid: 'total-incentive' },
              { label: 'Net Payable', value: data.totals.net, color: 'text-teal-700', testid: 'total-net' },
              { label: 'Paid', value: data.totals.paid || 0, color: 'text-emerald-700', testid: 'total-paid' },
              { label: 'Pending', value: data.totals.pending || 0, color: (data.totals.pending || 0) > 0 ? 'text-rose-700' : 'text-slate-400', testid: 'total-pending' },
            ].map((c) => (
              <div
                key={c.label}
                className="bg-white border border-slate-200 rounded-xl p-3"
                data-testid={c.testid || undefined}
              >
                <div className="text-xs text-slate-500 uppercase tracking-wider">{c.label}</div>
                <div className={`text-xl font-bold font-mono ${c.color}`}>{fmtINR(c.value)}</div>
              </div>
            ))}
          </div>

          {/* Staff payment status roll-up */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="status-paid">
              <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">Fully paid</div>
              <div className="text-2xl font-bold text-emerald-800">
                {data.totals.staff_paid || 0}
                <span className="text-sm text-emerald-600 font-normal"> / {data.totals.staff_total || 0}</span>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="status-partial">
              <div className="text-[11px] uppercase tracking-wider text-amber-700 font-semibold">Partially paid</div>
              <div className="text-2xl font-bold text-amber-800">
                {data.totals.staff_partial || 0}
                <span className="text-sm text-amber-600 font-normal"> / {data.totals.staff_total || 0}</span>
              </div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="status-pending">
              <div className="text-[11px] uppercase tracking-wider text-rose-700 font-semibold">Not paid yet</div>
              <div className="text-2xl font-bold text-rose-800">
                {data.totals.staff_pending || 0}
                <span className="text-sm text-rose-600 font-normal"> / {data.totals.staff_total || 0}</span>
              </div>
            </div>
          </div>

          <RatingBonusSettings onSaved={compute} />

          {/* Per-staff table */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-left">
                  <tr>
                    <th className="px-3 py-2 font-semibold text-slate-600">Staff</th>
                    <th className="px-3 py-2 font-semibold text-slate-600 text-center">Days</th>
                    <th className="px-3 py-2 font-semibold text-slate-600 text-right">Base</th>
                    <th className="px-3 py-2 font-semibold text-slate-600 text-right">OT Pay</th>
                    <th className="px-3 py-2 font-semibold text-slate-600 text-right">Incentive</th>
                    <th className="px-3 py-2 font-semibold text-slate-600 text-right">Net</th>
                    <th className="px-3 py-2 font-semibold text-slate-600 text-right">Paid</th>
                    <th className="px-3 py-2 font-semibold text-slate-600 text-right">Pending</th>
                    <th className="px-3 py-2 font-semibold text-slate-600 text-center">Status</th>
                    <th className="px-3 py-2 font-semibold text-slate-600 text-center w-16">PDF</th>
                    <th className="px-3 py-2 font-semibold text-slate-600 text-center w-16">Pay</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.rows.map((r) => (
                    <React.Fragment key={r.staff_id}>
                      <tr className="hover:bg-slate-50 cursor-pointer" onClick={() => setExpanded({ ...expanded, [r.staff_id]: !expanded[r.staff_id] })}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-slate-900">{r.staff_name}</div>
                          <div className="text-[11px] text-slate-500">{r.level} · ₹{r.incentive_per_vehicle}/veh · {r.ot_hours}h OT · {r.incentive_lines.length} veh</div>
                        </td>
                        <td className="px-3 py-2 text-center text-xs text-slate-600">{r.paid_days}/{r.days_in_month}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtINR(r.base_due)}</td>
                        <td className="px-3 py-2 text-right font-mono">{fmtINR(r.ot_pay)}</td>
                        <td className="px-3 py-2 text-right font-mono text-emerald-700">{fmtINR(r.incentive_total)}<RatingBonusNote rb={r.rating_bonus} staffId={r.staff_id} /></td>
                        <td className="px-3 py-2 text-right font-mono font-bold text-teal-700">{fmtINR(r.net_payable)}</td>
                        <td className="px-3 py-2 text-right font-mono text-emerald-700" data-testid={`row-paid-${r.staff_id}`}>{fmtINR(r.paid_amount || 0)}</td>
                        <td className="px-3 py-2 text-right font-mono" data-testid={`row-pending-${r.staff_id}`}>
                          <span className={(r.pending_amount || 0) > 0 ? 'text-rose-700 font-semibold' : 'text-slate-400'}>
                            {fmtINR(r.pending_amount || 0)}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center" data-testid={`row-status-${r.staff_id}`}>
                          {(() => {
                            const s = r.payment_status || 'pending';
                            const cfg = {
                              paid: { cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', label: 'Paid' },
                              partial: { cls: 'bg-amber-100 text-amber-800 border-amber-200', label: 'Partial' },
                              pending: { cls: 'bg-rose-100 text-rose-800 border-rose-200', label: 'Pending' },
                              zero: { cls: 'bg-slate-100 text-slate-500 border-slate-200', label: '—' },
                            }[s] || { cls: 'bg-slate-100 text-slate-500 border-slate-200', label: s };
                            return (
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cfg.cls}`}>{cfg.label}</span>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => downloadStatementPdf(r.staff_id, r.staff_name)}
                            className="p-1.5 rounded hover:bg-teal-50 text-teal-600 hover:text-teal-800"
                            title="Download monthly payroll statement (PDF)"
                            data-testid={`payroll-pdf-${r.staff_id}`}
                          >
                            <FileDown size={16} />
                          </button>
                        </td>
                        <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => openPaymentModal(r)}
                            className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600 hover:text-emerald-800"
                            title="Record / view payments for this staff this month"
                            data-testid={`payroll-pay-${r.staff_id}`}
                          >
                            <IndianRupee size={16} />
                          </button>
                        </td>
                      </tr>
                      {expanded[r.staff_id] && (
                        <tr>
                          <td colSpan={11} className="bg-slate-50 px-6 py-3">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                              <div>
                                <h4 className="font-semibold text-slate-700 mb-1">Attendance</h4>
                                <ul className="space-y-0.5">
                                  <li>Present: <b>{r.attendance.present || 0}</b></li>
                                  <li>Half day: <b>{r.attendance.half_day || 0}</b></li>
                                  <li>Paid leave: <b>{r.attendance.paid_leave || 0}</b></li>
                                  <li>Unpaid leave: <b>{r.attendance.unpaid_leave || 0}</b></li>
                                  <li>Absent: <b>{r.attendance.absent || 0}</b></li>
                                </ul>
                              </div>
                              <div>
                                <h4 className="font-semibold text-slate-700 mb-1">Incentive vehicles ({r.incentive_lines.length})</h4>
                                {r.incentive_lines.length === 0 ? (
                                  <p className="text-slate-400">No invoices credited this month.</p>
                                ) : (
                                  <ul className="space-y-0.5 max-h-32 overflow-y-auto">
                                    {r.incentive_lines.map((inv) => (
                                      <li key={inv.invoice_id}><span className="font-mono">{inv.invoice_number}</span> · {inv.date} — <b>{fmtINR(inv.amount)}</b></li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                              <div data-testid={`advance-panel-${r.staff_id}`}>
                                <h4 className="font-semibold text-slate-700 mb-1">
                                  Salary advances{' '}
                                  <span className="text-rose-700 font-normal">
                                    · deducted {fmtINR(r.advance_total || 0)}
                                  </span>
                                </h4>
                                {(r.advance_lines || []).length === 0 ? (
                                  <p className="text-slate-400 mb-1">No advances this month.</p>
                                ) : (
                                  <ul className="space-y-0.5 max-h-24 overflow-y-auto mb-2">
                                    {r.advance_lines.map((a) => (
                                      <li key={a.id} className="flex items-center justify-between gap-1">
                                        <span>{a.date} · <span className="text-slate-500">{a.note || '—'}</span></span>
                                        <span className="flex items-center gap-1">
                                          <b className="text-rose-700">({fmtINR(a.amount)})</b>
                                          <button
                                            type="button"
                                            onClick={() => deleteAdvance(r, a.id)}
                                            className="p-0.5 hover:bg-rose-100 rounded"
                                            title="Delete advance"
                                            data-testid={`delete-advance-${a.id}`}
                                          >
                                            <Trash2 size={11} className="text-rose-500" />
                                          </button>
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                <div
                                  className="flex flex-wrap items-end gap-1 pt-1 border-t border-slate-200"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <input
                                    type="date"
                                    value={(advForm[r.staff_id]?.date) || todayISO()}
                                    onChange={(e) => setAdvForm((p) => ({ ...p, [r.staff_id]: { ...(p[r.staff_id] || {}), date: e.target.value } }))}
                                    className={`${inputCls} !py-1 !text-[11px] !w-[105px]`}
                                    data-testid={`advance-date-${r.staff_id}`}
                                  />
                                  <input
                                    type="number" min="1" placeholder="Amount"
                                    value={(advForm[r.staff_id]?.amount) ?? ''}
                                    onChange={(e) => setAdvForm((p) => ({ ...p, [r.staff_id]: { ...(p[r.staff_id] || {}), amount: e.target.value } }))}
                                    className={`${inputCls} !py-1 !text-[11px] !w-[80px]`}
                                    data-testid={`advance-amount-${r.staff_id}`}
                                  />
                                  <input
                                    type="text" placeholder="Note (optional)"
                                    value={(advForm[r.staff_id]?.note) ?? ''}
                                    onChange={(e) => setAdvForm((p) => ({ ...p, [r.staff_id]: { ...(p[r.staff_id] || {}), note: e.target.value } }))}
                                    className={`${inputCls} !py-1 !text-[11px] flex-1 min-w-[80px]`}
                                    data-testid={`advance-note-${r.staff_id}`}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => submitAdvance(r)}
                                    disabled={advSaving === r.staff_id}
                                    className="inline-flex items-center gap-1 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-[11px] font-semibold px-2 py-1 rounded"
                                    data-testid={`add-advance-${r.staff_id}`}
                                  >
                                    <Plus size={10} /> Add
                                  </button>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-xs text-slate-500">Click a staff row to expand attendance, credited vehicles, and salary advances. Posting to Expenses creates one consolidated &quot;Salary &amp; Wages&quot; entry per staff (idempotent). Use the <b>₹</b> button to record partial payments — advances are auto-deducted from the net payable and appear on the PDF.</p>
        </>
      )}

      {/* Record Payment modal */}
      {payOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="payment-modal">
          <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b bg-gradient-to-r from-teal-600 to-teal-500 text-white">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wider opacity-90">Record payment</h2>
                <p className="text-lg font-bold">{payOpen.staff_name} <span className="text-xs font-normal opacity-80">· {new Date(year, month - 1, 1).toLocaleString('default', { month: 'long' })} {year}</span></p>
              </div>
              <button onClick={closePaymentModal} className="p-1.5 rounded hover:bg-white/20" data-testid="payment-close-btn"><X size={18} /></button>
            </div>

            {/* Summary chips */}
            <div className="grid grid-cols-3 gap-2 px-5 py-3 bg-slate-50 border-b border-slate-200">
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Net payable</div>
                <div className="font-mono font-bold text-slate-900 text-base">{fmtINR(payOpen.net_payable)}</div>
              </div>
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Paid so far</div>
                <div className="font-mono font-bold text-emerald-700 text-base" data-testid="payment-paid-total">{fmtINR(payments.total_paid)}</div>
              </div>
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">{outstanding > 0 ? 'Outstanding' : 'Settled'}</div>
                <div className={`font-mono font-bold text-base ${outstanding > 0 ? 'text-red-700' : 'text-emerald-700'}`} data-testid="payment-outstanding">
                  {outstanding > 0 ? fmtINR(outstanding) : '✓ ₹0'}
                </div>
              </div>
            </div>

            <div className="px-5 py-4 space-y-4 overflow-y-auto">
              {/* New payment form */}
              <div className="bg-emerald-50/60 border border-emerald-200 rounded-xl p-3 space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-emerald-800 flex items-center gap-1.5"><Plus size={12} /> New payment</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div>
                    <label className="block text-[11px] text-slate-500 mb-1">Date</label>
                    <input type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} className={inputCls} data-testid="payment-date" />
                  </div>
                  <div>
                    <label className="block text-[11px] text-slate-500 mb-1">Method</label>
                    <select value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} className={inputCls} data-testid="payment-method">
                      {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-[11px] text-slate-500 mb-1">Amount (₹)</label>
                    <input type="number" min="0" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className={`${inputCls} font-mono`} placeholder="0.00" data-testid="payment-amount" />
                  </div>
                </div>
                {/* Quick-fill chips */}
                <div className="flex flex-wrap gap-1.5">
                  <button type="button" onClick={() => setPayForm((f) => ({ ...f, amount: outstanding.toFixed(2) }))} disabled={outstanding <= 0} className="text-[11px] bg-white border border-emerald-300 hover:bg-emerald-100 text-emerald-800 px-2.5 py-1 rounded-full font-semibold disabled:opacity-40" data-testid="quickfill-outstanding">
                    Full outstanding · {fmtINR(outstanding)}
                  </button>
                  <button type="button" onClick={() => setPayForm((f) => ({ ...f, amount: (outstanding / 2).toFixed(2) }))} disabled={outstanding <= 0} className="text-[11px] bg-white border border-emerald-300 hover:bg-emerald-100 text-emerald-800 px-2.5 py-1 rounded-full font-semibold disabled:opacity-40" data-testid="quickfill-half">
                    Half · {fmtINR(outstanding / 2)}
                  </button>
                  <button type="button" onClick={() => setPayForm((f) => ({ ...f, amount: Number(payOpen.net_payable || 0).toFixed(2) }))} className="text-[11px] bg-white border border-slate-300 hover:bg-slate-100 text-slate-700 px-2.5 py-1 rounded-full font-semibold" data-testid="quickfill-fullnet">
                    Full net · {fmtINR(payOpen.net_payable)}
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <input type="text" placeholder="Reference / cheque no." value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} className={inputCls} data-testid="payment-reference" />
                  <input type="text" placeholder="Notes" value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} className={inputCls} data-testid="payment-notes" />
                </div>
                <button
                  onClick={savePayment}
                  disabled={paySaving || Number(payForm.amount || 0) <= 0}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white font-semibold py-2 rounded-lg inline-flex items-center justify-center gap-1.5"
                  data-testid="payment-save-btn"
                >
                  {paySaving ? <Loader2 className="animate-spin" size={14} /> : <IndianRupee size={14} />}
                  {paySaving ? 'Recording…' : `Record ₹${Number(payForm.amount || 0).toFixed(2)} payment`}
                </button>
              </div>

              {/* Existing payments list */}
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">Payment history · {payments.rows.length}</h3>
                {payLoading ? (
                  <div className="text-center py-6 text-slate-400 text-sm"><Loader2 className="animate-spin inline mr-2" size={14} /> Loading…</div>
                ) : payments.rows.length === 0 ? (
                  <div className="text-center py-6 bg-slate-50 rounded-lg text-slate-400 text-sm border border-slate-200 border-dashed">
                    No payments recorded for this period yet.
                  </div>
                ) : (
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50 border-b border-slate-200 text-left">
                        <tr>
                          <th className="px-3 py-1.5 font-semibold text-slate-600">Date</th>
                          <th className="px-3 py-1.5 font-semibold text-slate-600">Method</th>
                          <th className="px-3 py-1.5 font-semibold text-slate-600">Reference</th>
                          <th className="px-3 py-1.5 font-semibold text-slate-600 text-right">Amount</th>
                          <th className="px-2 py-1.5"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {payments.rows.map((p) => (
                          <tr key={p.id} className="hover:bg-slate-50" data-testid={`payment-row-${p.id}`}>
                            <td className="px-3 py-1.5 font-mono">{(p.date || '').slice(0, 10)}</td>
                            <td className="px-3 py-1.5">
                              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                p.method === 'Cash' ? 'bg-amber-100 text-amber-700'
                                  : p.method === 'UPI' ? 'bg-purple-100 text-purple-700'
                                    : 'bg-sky-100 text-sky-700'
                              }`}>{p.method || 'Bank'}</span>
                            </td>
                            <td className="px-3 py-1.5 text-slate-600 truncate max-w-[140px]">{p.reference || '—'}</td>
                            <td className="px-3 py-1.5 text-right font-mono font-semibold text-emerald-700">{fmtINR(p.amount)}</td>
                            <td className="px-2 py-1.5 text-right">
                              <button onClick={() => deletePayment(p.id)} className="p-1 rounded hover:bg-red-50 text-red-500" title="Delete payment" data-testid={`payment-delete-${p.id}`}>
                                <Trash2 size={12} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="px-5 py-3 border-t bg-slate-50 flex justify-between items-center">
              <p className="text-[11px] text-slate-500">Payments roll into Expenses (Salary &amp; Wages) and appear in the PDF.</p>
              <button onClick={closePaymentModal} className="text-sm bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold px-4 py-2 rounded-lg" data-testid="payment-done-btn">
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollPage;
